import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import throttle from 'lodash/throttle';
import cn from 'classnames';

import type { ICarouselState, TCarouselAction, TCarouselDesktop } from '../models';
import { moveLeft, moveRight } from '../utils/mover';
import { useAnalyticsView } from '../utils/use-analytics-view';

/** Зона, когда мы перестаем отображать скролл вправо */
const SAFE_ZONE = 19;

/** время троттлинга */
const THROTTLE_TIME = 50;

const initialState: ICarouselState = {
    cardSpacing: 0,
    currentScroll: 0,
    scrollWidth: 0,
    cardWidths: [],
    clientWidth: 0,
    canGoRight: false,
    canGoLeft: false,
};

const reducer = (state: ICarouselState, action: TCarouselAction) => {
    switch (action.type) {
        case 'reset':
            return { ...initialState, ...action.payload };
        case 'setscroll':
            return {
                ...state,
                currentScroll: action.payload,
                canGoLeft: action.payload > 0,
                canGoRight: action.payload + SAFE_ZONE < state.scrollWidth - state.clientWidth,
            };
        default:
            throw new Error();
    }
};

export const CardCarousel: FC<TCarouselDesktop> = props => {
    const {
        FontStyreneText,
        Grid,
        Layout,
        NavContainer,
        Link,
        Text,
        calculateCardWidths,
        children,
        config: {
            card: { containerWidth, spacing },
        },
        dataTestId,
        headerBottomPadding = 32,
        headingText,
        subheadingText,
        headingUrl,
        headingColor = 'light',
        headingVariant = 'main',
        id,
        layoutWidth = containerWidth,
        parseNewLines,
        mapSpacerVerticalToStyler,
        fullWidth,
        renderCards,
        styles,
        theme = 'light',
        analytics,
        sendEvent,
        className,
    } = props;
    const [state, dispatch] = useReducer(reducer, initialState);
    const { canGoLeft, canGoRight } = state;

    const ref = useRef<HTMLDivElement>(null);

    /*  Не нужен children в массиве зависимостей.
        Контент статичный + из-за этой зависимости срабатываает dispatch reset,
        что приводит к неправильному рендеру интерфейса
    */
    const cardWidths = useMemo(
        () => calculateCardWidths(children, layoutWidth, spacing),
        [calculateCardWidths, layoutWidth, spacing]
    );

    const scrollWidth = useMemo(
        () =>
            cardWidths.reduce((prev, next) => {
                return prev + next + spacing;
            }, 0) - spacing,
        [cardWidths, spacing]
    );

    useAnalyticsView(ref, children);

    useEffect(() => {
        const onScroll = () => {
            const xOffset = ref.current?.scrollLeft;

            dispatch({
                type: 'setscroll',
                payload: xOffset ?? 0,
            });
        };

        const throttled = throttle(onScroll, THROTTLE_TIME);

        if (ref.current) {
            const node = ref.current;

            node.addEventListener('scroll', throttled, true);

            const { offsetWidth: clientWidth } = ref.current;

            dispatch({
                type: 'reset',
                payload: {
                    clientWidth,
                    scrollWidth,
                    cardWidths,
                    cardSpacing: spacing,
                    canGoRight: scrollWidth > clientWidth,
                },
            });

            return () => {
                node.removeEventListener('scroll', throttled);
            };
        }

        return () => {};
    }, [cardWidths, spacing, scrollWidth]);

    // ResizeObserver нужен для случаев, когда карусель прячут за display:none, и в какой-то момент переключают на display:block
    // Например, Tab с параметром keepMounted. При первом рендере карусель получает clientWidth=0
    // и при включении таба уже не пересчитывает свои размеры, в итоге возникают разные баги
    useEffect(() => {
        if (ref.current && state.clientWidth === 0 && 'ResizeObserver' in window) {
            const resizeObserver = new ResizeObserver(entries => {
                entries.forEach(entry => {
                    dispatch({
                        type: 'reset',
                        payload: {
                            ...state,
                            scrollWidth,
                            cardSpacing: spacing,
                            cardWidths,
                            clientWidth: entry.target.clientWidth,
                            canGoRight: scrollWidth > entry.target.clientWidth,
                        },
                    });
                });
            });

            resizeObserver.observe(ref.current);

            return () => {
                if (ref.current) {
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    resizeObserver.unobserve(ref.current);
                }
            };
        }
    }, [cardWidths, scrollWidth, spacing, state]);

    const scrollTo = useCallback(
        (left: number) => {
            sendEvent(analytics);

            if (ref.current) {
                ref.current.scrollTo({
                    top: 0,
                    left,
                    behavior: 'smooth',
                });
            }
        },
        [analytics, sendEvent]
    );

    const onLeft = () => scrollTo(moveLeft(state));
    const onRight = () => scrollTo(moveRight(state));

    const carousel = (
        <NavContainer
            {...props}
            canGoLeft={canGoLeft}
            canGoRight={canGoRight}
            dataTestId={dataTestId}
            onLeft={onLeft}
            onRight={onRight}
        >
            <div ref={ref} className={styles.gridContainer}>
                <Grid
                    classNames={{
                        col: cn(styles.external, styles.colFlex),
                    }}
                    columnsWidth={cardWidths}
                    justify="start"
                >
                    {renderCards(props, cardWidths)}
                </Grid>
            </div>
        </NavContainer>
    );

    const heading = parseNewLines(headingText);

    const headingComponent = (
        <Text
            styler={{
                marginTop: 0,
                marginBottom: subheadingText ? 16 : mapSpacerVerticalToStyler(headerBottomPadding),
            }}
            tag="h2"
            theme={headingColor}
            view="medium"
        >
            {headingUrl ? <Link href={headingUrl}>{heading}</Link> : heading}
        </Text>
    );

    return (
        <div
            className={cn(styles.layout, styles[theme], className)}
            data-test-id={dataTestId}
            id={id}
        >
            <Layout hasPadding={false}>
                {headingText && headingVariant === 'main' && headingComponent}
                {headingText && headingVariant === 'fancy' && (
                    <FontStyreneText>{headingComponent}</FontStyreneText>
                )}
                {subheadingText && (
                    <Text
                        styler={{ marginTop: 0, marginBottom: 32 }}
                        tag="div"
                        view="primary-medium"
                    >
                        {subheadingText}
                    </Text>
                )}
                {!fullWidth && carousel}
            </Layout>
            {fullWidth && carousel}
        </div>
    );
};
